import React, { useState, createContext, useContext } from 'react';
import { XIcon } from 'lucide-react';
type ToastType = 'success' | 'error' | 'info' | 'warning';
type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
interface Toast {
  id: string;
  title: string;
  message: string;
  type: ToastType;
  duration: number;
}
interface ToastContextType {
  success: (title: string, message: string, duration?: number) => void;
  error: (title: string, message: string, duration?: number) => void;
  info: (title: string, message: string, duration?: number) => void;
  warning: (title: string, message: string, duration?: number) => void;
}
const ToastContext = createContext<ToastContextType>({
  success: () => {},
  error: () => {},
  info: () => {},
  warning: () => {}
});
export const useToast = () => useContext(ToastContext);
export const ToastContainer: React.FC<{
  position?: ToastPosition;
}> = ({
  position = 'bottom-right'
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = (title: string, message: string, type: ToastType, duration: number = 5000) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast = {
      id,
      title,
      message,
      type,
      duration
    };
    setToasts(prev => [...prev, newToast]);
    setTimeout(() => {
      removeToast(id);
    }, duration);
  };
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };
  const getPositionClasses = () => {
    switch (position) {
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 -translate-x-1/2';
      case 'bottom-center':
        return 'bottom-4 left-1/2 -translate-x-1/2';
    }
  };
  const getToastClasses = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900/30 border-green-500 dark:border-green-500';
      case 'error':
        return 'bg-red-100 dark:bg-red-900/30 border-red-500 dark:border-red-500';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-500 dark:border-yellow-500';
      case 'info':
        return 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 dark:border-blue-500';
    }
  };
  const getToastTextClasses = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'text-green-700 dark:text-green-300';
      case 'error':
        return 'text-red-700 dark:text-red-300';
      case 'warning':
        return 'text-yellow-700 dark:text-yellow-300';
      case 'info':
        return 'text-blue-700 dark:text-blue-300';
    }
  };
  return <ToastContext.Provider value={{
    success: (title, message, duration) => addToast(title, message, 'success', duration),
    error: (title, message, duration) => addToast(title, message, 'error', duration),
    info: (title, message, duration) => addToast(title, message, 'info', duration),
    warning: (title, message, duration) => addToast(title, message, 'warning', duration)
  }} data-id="element-1551">
      <div className={`fixed z-50 flex flex-col gap-2 max-w-md ${getPositionClasses()}`} data-id="element-1552">
        {toasts.map(toast => <div key={toast.id} className={`rounded-lg border-l-4 p-4 shadow-md animate-slideIn ${getToastClasses(toast.type)}`} role="alert" data-id="element-1553">
            <div className="flex items-start justify-between" data-id="element-1554">
              <div data-id="element-1555">
                <h3 className={`font-medium ${getToastTextClasses(toast.type)}`} data-id="element-1556">
                  {toast.title}
                </h3>
                <div className="mt-1 text-sm text-gray-600 dark:text-gray-300" data-id="element-1557">
                  {toast.message}
                </div>
              </div>
              <button onClick={() => removeToast(toast.id)} className="ml-4 inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700" data-id="element-1558">
                <XIcon className="h-4 w-4" data-id="element-1559" />
              </button>
            </div>
          </div>)}
      </div>
    </ToastContext.Provider>;
};